import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les points sont leur propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'morph',
  family: 'particles',
  danceability: 0.88,
  requiresTier: 0,
  energyLevel: 0.84,
  bpmMin: 115,
  bpmMax: 175,
  movement: 0.85,
  tonality: 0.45,
  enabled: true
};

// MORPH : 24 000 points lumineux qui forment des volumes AUTOUR de la
// pochette (tore, sphère creuse, double hélice, cube, champ de lignes, nappe)
// et se transforment de l'un à l'autre sur les phrases ; la musique hérisse
// la surface. Techno, house, électro, bass music, trance. LOOK VDJ party.
//
// TOUT EST DANS LE VERTEX SHADER (critère 5) : chaque point n'a que des
// attributs statiques (u, v, aléa). Sa position sur chaque forme est une
// fonction analytique de (u, v) ; la même grille sert à toutes les formes et
// u y est, autant que possible, l'azimut autour de la pochette : un point
// reste du même côté d'une forme à l'autre, le morph ne croise pas. Le CPU
// n'écrit que des scalaires et deux matrices de rotation par frame.
//
// LE MORPH (2 temps) : chaque point part avec un retard proportionnel à u
// (la forme se défait et se refait comme une vague), interpole entre ses
// deux positions et s'écarte en arc perpendiculaire (sin(π e)).
//
// LA SURFACE : des pointes poussées le long de la normale de la forme,
// concentrées en cônes (cellules de la grille (u, v), une sur trois active),
// les zones basses de la forme aux graves (le kick, à chaque temps), les
// hautes aux aigus (les croches) ; montée en 20 ms, retombée à 5 % en
// 150 ms. Une onde de déplacement parcourt u à chaque temps. La forme tourne
// sur elle-même (un tour en 16 temps, axe changé sur la phrase ; le tore
// tourne autour de son axe, l'hélice autour de la verticale).
//
// LA POCHETTE (critère 1) : les formes sont dimensionnées entre la zone de
// sécurité et le bord ; et TOUT point dont la projection tombe dans le
// rectangle de la pochette (+ marge) ou dans une zone d'exclusion de
// l'interface s'éteint dans le vertex shader - rotation, pointes, morph et
// explosion compris. La face avant de la sphère, celle du cube, l'hélice qui
// passe devant : découpées en un trou propre autour de la pochette.
//
// LE RENDU : un seul draw call de points dans une cible pleine résolution ;
// un FeedbackBuffer demi-résolution en garde les traînées (demi-vie d'un
// demi-temps, deux temps pendant l'explosion), décroissance multiplicative
// puis -1/255 à chaque frame : un buffer laissé seul revient au noir pur
// (R15). L'écran = traînées + points nets par-dessus.

const TAU = Math.PI * 2;
const SHAPES = ['ring', 'shell', 'helix', 'cube', 'field', 'wave'];
const GRID_W = 400;                       // u samples; v rows = points / GRID_W
const TIER = {
  0: { points: 8000, trails: false },
  1: { points: 16000, trails: true },
  2: { points: 24000, trails: true }
};
const FOV_DEG = 40;
const CAM_D = 1 / Math.tan(FOV_DEG * Math.PI / 360);   // the z = 0 plane spans the screen's height (units = screen heights / 2)
const MORPH_BEATS = 2, ROT_BEATS = 16;
const SPIKE_RISE_S = 0.02, SPIKE_TAU_S = 0.045;        // 5 % left at 150 ms
const BOOM_BURST_BEATS = 1, BOOM_GATHER_BEATS = 4;
const BUILD_BEATS = 32;                                 // the rise: the 8 bars before a known drop
const TRAIL_HALF_BEATS = 0.5, TRAIL_HALF_BOOM_BEATS = 0.75;   // 2 beats kept the old shape as a ghost through the burst
const NO_BPM = 128, SNAP_BEATS = 0.35;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const EXCLUSIONS = [['tr', 0.22, 0.12], ['tl', 0.08, 0.12], ['br', 0.22, 0.10]];

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
// the spike envelope: 20 ms up, exponential down (5 % at 150 ms)
export function spikeEnvelope(t) {
  if (t < 0) return 0;
  if (t < SPIKE_RISE_S) return t / SPIKE_RISE_S;
  return Math.exp(-(t - SPIKE_RISE_S) / SPIKE_TAU_S);
}

// --- GLSL ---------------------------------------------------------------------
const pointsVertexShader = `
  precision highp float;
  const float PI = 3.14159265;
  const float TAU = 6.2831853;
  uniform float uShapeA;
  uniform float uShapeB;
  uniform float uMorph;        // 0..1, the morph's progression
  uniform mat3 uRotA;
  uniform mat3 uRotB;
  uniform float uSpinA;        // the ring turns about its own axis by its parameter (it is wider than tall)
  uniform float uSpinB;
  uniform vec4 uCover;         // centre x, y, half x, y (screen units)
  uniform float uClear;        // 1: the cover is at the centre (keep its clearance), 0: it is in a corner
  uniform vec4 uGuard;         // the REAL cover (R19): centre x, y, half x, y - the guard's hole follows it
  uniform vec2 uFrame;         // usable half extents (screen units)
  uniform float uAspect;
  uniform float uContract;     // the rise: 0..1
  uniform vec4 uSpike;         // low envelope, high envelope, amplitude, cell density
  uniform vec3 uWave;          // front (0..1 along u), amplitude, width
  uniform float uBeats;        // the clock (scrolls)
  uniform vec3 uBoom;          // active, burst (0..1), gather (0..1)
  uniform vec3 uCol1;
  uniform vec3 uCol2;
  uniform float uScroll;
  uniform float uGain;         // bar flash, comfort
  uniform float uPx;           // pixels per "pixel" of the brief (target height / 1080)
  varying vec3 vCol;
  varying float vAlpha;

  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

  // the room each shape has: from the cover's clearance to the frame's edge
  // the clearance round the centre: the cover's room while the cover is at the
  // centre; closed (a small hole only) once it sits in a corner - an empty hole
  // left at the centre of the screen read as a black space reserved for nothing
  float rIn() { return mix(0.14, max(uCover.z, uCover.w) * 1.10 + 0.06, uClear); }
  float rUp() { return uFrame.y - uCover.y; }
  float rDown() { return uFrame.y + uCover.y; }
  float rV() { return min(rUp(), rDown()) * 0.95; }
  float rX() { return (uFrame.x - abs(uCover.x)) * 0.95; }

  // a shape: position (relative to the cover's centre, z toward the viewer) and normal
  void shapeAt(float s, vec2 uv, float rnd, float spin, out vec3 p, out vec3 n) {
    float u = uv.x, v = uv.y;
    if (s < 0.5) {
      // RING: a thick torus, its hole round the cover, a little wider than tall on a wide screen
      float Ri = rIn() * 1.12, Ro = rV();          // tilted, the hole's top comes down: more clearance
      float Rm = 0.5 * (Ri + Ro), rt = 0.5 * (Ro - Ri) * 0.85;
      float ex = clamp(rX() / Ro, 1.0, 1.45);
      float th = TAU * (u + spin), ph = TAU * v;
      float rr = Rm + rt * cos(ph);
      p = vec3(rr * cos(th) * ex, rr * sin(th), rt * sin(ph));
      n = normalize(vec3(cos(ph) * cos(th) / ex, cos(ph) * sin(th), sin(ph)));
    } else if (s < 1.5) {
      // SHELL: a hollow sphere round the cover (its front cut by the guard)
      float Rs = min(rV(), rX()) * 0.97;
      Rs *= sqrt(1.0 - Rs * Rs / ${(CAM_D * CAM_D).toFixed(4)});     // seen from D, the silhouette is R D / sqrt(D² - R²)
      float th = TAU * u, al = PI * (0.02 + 0.96 * v);
      n = vec3(sin(al) * cos(th), sin(al) * sin(th), cos(al));
      p = n * Rs;
    } else if (s < 2.5) {
      // HELIX: two strands round the vertical through the cover
      float k = step(0.5, v);
      float Rh = clamp(rX() * 0.62, rIn() * 0.9, 1.25);
      // the strands in front (z up to Rh) are magnified by the perspective: the height shrinks by it
      float y = mix(-rDown(), rUp(), u) * 0.92 * (${CAM_D.toFixed(4)} - Rh) / ${CAM_D.toFixed(4)};
      float ph = TAU * 2.5 * u + k * PI;
      vec3 rad = vec3(cos(ph), 0.0, sin(ph));
      vec3 c = vec3(Rh * cos(ph), y, Rh * sin(ph));
      float ps = TAU * fract(v * 2.0);
      n = cos(ps) * rad + sin(ps) * vec3(0.0, 1.0, 0.0);
      p = c + 0.055 * n;
    } else if (s < 3.5) {
      // CUBE: edges (55 % of the points) and faces in points
      float Hc = min(rV(), rX()) / 1.72;
      Hc *= ${CAM_D.toFixed(4)} / (${CAM_D.toFixed(4)} + 1.2 * Hc);     // its near corners are magnified
      if (v < 0.55) {
        float e = floor(v / 0.55 * 12.0);
        float ax = floor(e / 4.0), sg = mod(e, 4.0);
        float s1 = mod(sg, 2.0) * 2.0 - 1.0, s2 = floor(sg / 2.0) * 2.0 - 1.0;
        float t = 2.0 * u - 1.0;
        vec3 q = ax < 0.5 ? vec3(t, s1, s2) : (ax < 1.5 ? vec3(s1, t, s2) : vec3(s1, s2, t));
        p = q * Hc;
        n = normalize(q * vec3(ax < 0.5 ? 0.0 : 1.0, ax > 0.5 && ax < 1.5 ? 0.0 : 1.0, ax > 1.5 ? 0.0 : 1.0));
      } else {
        float f = floor((v - 0.55) / 0.45 * 6.0);
        float ax = floor(f / 2.0), sg = mod(f, 2.0) * 2.0 - 1.0;
        float a = 2.0 * u - 1.0, b = 2.0 * rnd - 1.0;
        vec3 q = ax < 0.5 ? vec3(sg, a, b) : (ax < 1.5 ? vec3(a, sg, b) : vec3(a, b, sg));
        p = q * Hc;
        n = ax < 0.5 ? vec3(sg, 0.0, 0.0) : (ax < 1.5 ? vec3(0.0, sg, 0.0) : vec3(0.0, 0.0, sg));
      }
    } else if (s < 4.5) {
      // FIELD: horizontal lines in perspective, a floor under the cover and a
      // ceiling over it, scrolling toward the viewer; normals toward the cover's plane
      float rows = 40.0;
      float line = floor(v * rows);
      float up = mod(line, 2.0);
      float k = floor(line / 2.0) / (rows * 0.5 - 1.0);
      float z = mix(1.15, -6.0, pow(fract(k + uBeats / 8.0 / (rows * 0.5)), 0.7));
      float nearScale = ${CAM_D.toFixed(4)} / (${CAM_D.toFixed(4)} - 1.15);
      float Y = up > 0.5 ? rUp() * 0.92 / nearScale : rDown() * 0.92 / nearScale;
      float X = (uFrame.x + 0.3) * 1.25;
      p = vec3((2.0 * u - 1.0) * X, up > 0.5 ? Y : -Y, z);
      n = vec3(0.0, up > 0.5 ? -1.0 : 1.0, 0.0);
    } else {
      // WAVE: a tilted sheet behind the cover, rolling
      float zc = -1.6;
      float sc = ${CAM_D.toFixed(4)} / (${CAM_D.toFixed(4)} - zc);
      float X = uFrame.x / sc * 1.05, Y = uFrame.y / sc * 1.05;
      float x = (2.0 * u - 1.0) * X, y = (2.0 * v - 1.0) * Y;
      float ph = 2.2 * x + 1.4 * y - TAU * uBeats / 8.0;
      float h = 0.22 * sin(ph) + 0.10 * sin(1.7 * x - 2.3 * y + TAU * uBeats / 16.0);
      float dhx = 0.22 * 2.2 * cos(ph) + 0.10 * 1.7 * cos(1.7 * x - 2.3 * y + TAU * uBeats / 16.0);
      float dhy = 0.22 * 1.4 * cos(ph) - 0.10 * 2.3 * cos(1.7 * x - 2.3 * y + TAU * uBeats / 16.0);
      float tl = 0.45, c = cos(tl), sn = sin(tl);
      vec3 q = vec3(x, y, h);
      p = vec3(q.x, c * q.y - sn * q.z, sn * q.y + c * q.z) + vec3(0.0, 0.0, zc);
      vec3 nn = normalize(vec3(-dhx, -dhy, 1.0));
      n = vec3(nn.x, c * nn.y - sn * nn.z, sn * nn.y + c * nn.z);
    }
  }

  void main() {
    vec2 uv = position.xy;
    float u = uv.x;
    float rnd = position.z;
    vec3 pa, na, pb, nb;
    shapeAt(uShapeA, uv, rnd, uSpinA, pa, na);
    pa = uRotA * pa; na = uRotA * na;
    vec3 p = pa, n = na;
    if (uMorph > 0.0) {
      shapeAt(uShapeB, uv, rnd, uSpinB, pb, nb);
      pb = uRotB * pb; nb = uRotB * nb;
      // the wave: each point starts after a delay set by its u
      float spread = 0.55;
      float e = clamp((uMorph * (1.0 + spread) - spread * u), 0.0, 1.0);
      e = e * e * (3.0 - 2.0 * e);
      vec3 d = pb - pa;
      vec3 side = cross(d, vec3(0.0, 0.0, 1.0));
      side = dot(side, side) > 1e-8 ? normalize(side) : vec3(0.0, 1.0, 0.0);
      p = mix(pa, pb, e) + side * length(d) * 0.22 * sin(PI * e);
      n = normalize(mix(na, nb, e) + vec3(1e-4));
    }
    // the rise: the shape contracts toward the cover
    p *= 1.0 - 0.28 * uContract;

    // the surface: spikes in cones (cells of the (u, v) grid), lows / highs by height
    vec2 cell = uv * vec2(48.0, 14.0);
    vec2 ci = floor(cell), cf = fract(cell) - 0.5;
    float on = step(1.0 - uSpike.w, hash2(ci));
    float cone = max(0.0, 1.0 - length(cf) * 2.0);
    cone = cone * cone;
    float yN = clamp(p.y / max(rV(), 0.2), -1.0, 1.0);
    float wLow = smoothstep(0.4, -0.7, yN);
    float env = mix(uSpike.y, uSpike.x, wLow);
    float spike = uSpike.z * env * cone * on * (0.6 + 0.4 * hash2(ci + 7.0));
    // the beat's wave along u
    float du = abs(fract(u - uWave.x + 0.5) - 0.5);
    float wave = uWave.y * exp(-du * du / (uWave.z * uWave.z));
    // along the normal AND outward round the cover: a normal facing the viewer
    // moved a spike toward the eye, invisible on screen
    vec2 rad2 = p.xy;
    vec3 outw = dot(rad2, rad2) > 1e-6 ? vec3(normalize(rad2), 0.0) : n;
    vec3 sdir = normalize(n + 0.9 * outw * (1.0 - abs(n.y) * 0.3));
    p += sdir * spike + n * wave;

    // the drop: a radial burst, then the gathering into the new shape (uMorph carries it)
    if (uBoom.x > 0.5) {
      vec3 dir = normalize(p + vec3(0.0, 0.0, 0.001) + (vec3(hash2(uv), hash2(uv + 3.1), hash2(uv + 5.7)) - 0.5) * 0.3);
      float b = uBoom.y;
      float dist = (1.7 * b - 0.8 * b * b) * (0.55 + 0.9 * rnd * rnd);
      p += dir * dist * (1.0 - uBoom.z);
    }

    vec3 world = p + vec3(uCover.xy, 0.0);
    vec4 mv = viewMatrix * vec4(world, 1.0);
    gl_Position = projectionMatrix * mv;

    // THE GUARD: nothing over the cover, nothing in the interface's corners.
    // The shapes are built round the stage (the centre of the screen), but the
    // hole is cut round the cover WHERE IT IS: with the cover in a corner, a
    // hole left at the centre was an empty black box (R19)
    vec2 s = gl_Position.xy / gl_Position.w * vec2(uAspect, 1.0);
    vec2 q = abs(s - uGuard.xy) - (uGuard.zw + 0.035);
    float dBox = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float alpha = smoothstep(0.0, 0.04, dBox);
    // the 5 % margins (R6): the floor and the sheet reach the frame's edge by construction
    vec2 mq = abs(s) - vec2(uAspect - 0.10, 0.90);
    alpha *= 1.0 - smoothstep(-0.08, 0.0, max(mq.x, mq.y));
    for (int i = 0; i < 3; i++) {
      vec4 ez = i == 0 ? vec4(uAspect - ${(0.22 * 2).toFixed(2)} * uAspect, 1.0 - ${(0.12 * 2).toFixed(2)}, uAspect, 1.0)
              : (i == 1 ? vec4(-uAspect, 1.0 - ${(0.12 * 2).toFixed(2)}, -uAspect + ${(0.08 * 2).toFixed(2)} * uAspect, 1.0)
                        : vec4(uAspect - ${(0.22 * 2).toFixed(2)} * uAspect, -1.0, uAspect, -1.0 + ${(0.10 * 2).toFixed(2)}));
      vec2 c = 0.5 * (ez.xy + ez.zw), h = 0.5 * (ez.zw - ez.xy);
      vec2 qq = abs(s - c) - h;
      float de = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
      alpha *= smoothstep(0.0, 0.14, de);          // soft: a hard box showed as a black rectangle once the burst filled the frame
    }

    // colour along u, scrolling; white core where the point is pushed out
    float g = 0.5 + 0.5 * cos(TAU * (u + uScroll));
    vec3 col = mix(uCol1, uCol2, g);
    float push = clamp((spike + wave) / max(uSpike.z * 0.55, 1e-3), 0.0, 1.0);
    col = mix(col, vec3(1.0), smoothstep(0.35, 0.9, push));
    // depth: the back of the shape at 40 %, the far smaller
    float zr = clamp(p.z / max(rV(), 0.3), -1.0, 1.0);
    float depth = mix(0.40, 1.0, smoothstep(-0.8, 0.6, zr));
    // a silhouette piles points up (a white rim round the shell): dim them by how edge-on the surface is
    vec3 nv = normalize(mat3(viewMatrix) * n);
    // (closed volumes only: the field's lines are edge-on by nature)
    float limbOn = mix(step(uShapeA, 3.5), step(uShapeB, 3.5), clamp(uMorph, 0.0, 1.0));
    depth *= mix(1.0, 0.15 + 0.85 * abs(nv.z), limbOn);
    float near = smoothstep(${(CAM_D + 2.0).toFixed(2)}, ${(CAM_D - 0.8).toFixed(2)}, -mv.z);
    gl_PointSize = mix(1.5, 3.0, near) * uPx;
    // the same points on less room pile up and bloom (the portrait's cube whitened the
    // frame): the gain follows the room the shapes have, 1 at 16:9
    float room = clamp(sqrt(rV() * rX() / 1.15), 0.40, 1.0);
    vCol = col * depth * uGain * room;
    vAlpha = alpha;
    if (alpha < 0.002) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  }
`;
const pointsFragmentShader = `
  precision mediump float;
  varying vec3 vCol;
  varying float vAlpha;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r = length(d) * 2.0;
    float a = 1.0 - smoothstep(0.55, 1.0, r);
    gl_FragColor = vec4(vCol * a * vAlpha, 1.0);
  }
`;
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// the trails: decay, then 1/255 off every frame (back to pure black, R15), the points injected
const feedbackFragmentShader = `
  precision mediump float;
  uniform sampler2D uPrev;
  uniform sampler2D uPts;
  uniform float uDecay;
  uniform float uInject;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uPrev, vUv).rgb * uDecay - vec3(1.0 / 255.0);
    c = max(c, 0.0) + texture2D(uPts, vUv).rgb * uInject;
    gl_FragColor = vec4(min(c, 1.0), 1.0);
  }
`;
const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uPts;
  uniform sampler2D uTrail;
  uniform float uTrailGain;
  uniform float uFlash;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uPts, vUv).rgb + texture2D(uTrail, vUv).rgb * uTrailGain;
    c = mix(c, vec3(${VDJ_DROP_FLASH.toFixed(2)}), uFlash);
    gl_FragColor = vec4(c, 1.0);
  }
`;

// the rotation axes the phrases pick from; the ring spins about its own axis
// (z, tilted), the helix about the vertical, the field and the wave do not
// turn (they scroll)
const AXES = [[0.3, 1, 0.2], [1, 0.4, 0], [0.2, 0.8, 0.6], [0.7, 0.7, -0.3], [-0.5, 1, 0.3]].map((a) => new THREE.Vector3(...a).normalize());
const _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _m4 = new THREE.Matrix4();
export function shapeRotation(shape, angle, axis, out) {
  const name = SHAPES[shape];
  if (name === 'ring') _q.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -0.32);   // seen from the front, a little tilted; it spins by its parameter else if (name === 'helix') _q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), angle);
  else if (name === 'cube') {
    _q.setFromAxisAngle(axis, angle);
    _q2.setFromEuler(new THREE.Euler(0.45, 0.62, 0));                 // three quarters
    _q.multiply(_q2);
  } else if (name === 'shell') _q.setFromAxisAngle(axis, angle);
  else _q.identity();
  _m4.makeRotationFromQuaternion(_q);
  return out.setFromMatrix4(_m4);
}

export class MorphVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    this._L = TIER[this._tier];
    const fs = urlParam('morphshape');
    this._forceShape = fs && SHAPES.includes(fs) ? SHAPES.indexOf(fs) : null;
    const sp = parseFloat(urlParam('morphspikes'));
    this._spikeMul = Number.isFinite(sp) ? sp : 1;
    const mt = parseFloat(urlParam('morpht'));
    this._forceMorph = Number.isFinite(mt) ? Math.max(0, Math.min(1, mt)) : null;
    this._forceDrop = urlParam('morphdrop') === '1';
    this._forceBuild = urlParam('morphbuild') === '1';
    this._post = new VdjPost({ bloomStrength: 0.55, bloomThreshold: 0.78, grain: 0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    if (urlParam('morphprobe') === '1' && typeof window !== 'undefined') window.__morph = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const L = this._L;
    this._cam = new THREE.PerspectiveCamera(FOV_DEG, 1.78, 0.05, 50);
    this._cam.position.set(0, 0, CAM_D);
    this._cam.lookAt(0, 0, 0);
    this._private = new THREE.Scene();

    // the points: static attributes only - (u, v, random)
    const n = L.points, rows = Math.round(n / GRID_W);
    const pos = new Float32Array(GRID_W * rows * 3);
    let seed = 12345;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    for (let j = 0; j < rows; j++) for (let i = 0; i < GRID_W; i++) {
      const o = (j * GRID_W + i) * 3;
      pos[o] = (i + 0.5) / GRID_W; pos[o + 1] = (j + 0.5) / rows; pos[o + 2] = rnd();
    }
    this._geom = new THREE.BufferGeometry();
    this._geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this._U = {
      uShapeA: { value: 0 }, uShapeB: { value: 0 }, uMorph: { value: 0 },
      uRotA: { value: new THREE.Matrix3() }, uRotB: { value: new THREE.Matrix3() }, uSpinA: { value: 0 }, uSpinB: { value: 0 },
      uCover: { value: new THREE.Vector4(0, 0, 0.36, 0.36) }, uGuard: { value: new THREE.Vector4(0, 0, 0.36, 0.36) }, uClear: { value: 1 }, uFrame: { value: new THREE.Vector2(1.68, 0.9) }, uAspect: { value: 1.78 },
      uContract: { value: 0 }, uSpike: { value: new THREE.Vector4(0, 0, 0.12, 0.33) }, uWave: { value: new THREE.Vector3(0, 0, 0.05) },
      uBeats: { value: 0 }, uBoom: { value: new THREE.Vector3(0, 0, 0) },
      uCol1: { value: new THREE.Color(1, 0, 0.5) }, uCol2: { value: new THREE.Color(0, 0.6, 1) }, uScroll: { value: 0 },
      uGain: { value: 1 }, uPx: { value: 1 }
    };
    this._points = new THREE.Points(this._geom, new THREE.ShaderMaterial({
      uniforms: this._U, vertexShader: pointsVertexShader, fragmentShader: pointsFragmentShader,
      transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
    }));
    this._points.frustumCulled = false;
    this._private.add(this._points);

    // the targets: the points at full resolution, the trails at half
    this._ptsTarget = null;
    this._fb = L.trails ? new FeedbackBuffer({ scale: 0.5 }) : null;
    this._fbU = { uPrev: { value: null }, uPts: { value: null }, uDecay: { value: 0.9 }, uInject: { value: 0.8 } };
    this._fbScene = new THREE.Scene();
    this._fbQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._fbU, vertexShader: quadVertexShader, fragmentShader: feedbackFragmentShader, depthTest: false, depthWrite: false
    }));
    this._fbQuad.frustumCulled = false; this._fbScene.add(this._fbQuad);
    this._fbCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._black = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat); this._black.needsUpdate = true;
    this._dispU = { uPts: { value: null }, uTrail: { value: this._black }, uTrailGain: { value: 0.75 }, uFlash: { value: 0 } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._dispU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // state
    this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._time = 0;
    this._shape = this._forceShape != null ? this._forceShape : 0;
    this._next = null; this._morphStart = null;
    this._rotAngle = 0; this._axisIdx = 0; this._rotAxisB = 0;
    this._lastKickS = -9; this._lastHatS = -9; this._lastBarS = -9;
    this._boom = null; this._flashFrames = 0; this._dt = 1 / 60;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteKey = null; this._lastPhraseAt = 0;
    this._log = [];
  }

  _note(e) { this._log.push(e); if (this._log.length > 40) this._log.shift(); }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  _nextShape() { return this._forceShape != null ? this._forceShape : (this._shape + 1) % SHAPES.length; }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._dt = dt;
    this._layout();
    const pal = profile.artworkPalette;
    const pk = pal ? String((pal.vividColors || []).slice(0, 3)) : '';
    if (pk !== this._paletteKey) {
      this._paletteKey = pk;
      const src = pal && (pal.vividColors || pal.dominantColors) || [];
      const c1 = vdjColor(src[0] || '#ff2a8a'), hsl = { h: 0, s: 0, l: 0 };
      c1.getHSL(hsl);
      // the second colour: the palette's next hue at least 0.25 away, else the complement-ish
      let c2 = null;
      for (const c of src.slice(1)) { const t = vdjColor(c), h2 = { h: 0, s: 0, l: 0 }; t.getHSL(h2); const d = Math.abs(h2.h - hsl.h); if (Math.min(d, 1 - d) >= 0.25) { c2 = t; break; } }
      if (!c2) c2 = new THREE.Color().setHSL((hsl.h + 0.42) % 1, 0.9, 0.55);
      this._U.uCol1.value.copy(c1); this._U.uCol2.value.copy(c2);
    }
    if (profile.playbackState !== 'PLAYING') { this._flashFrames = 0; this._dispU.uFlash.value = 0; return; }   // frozen
    this._time += dt;

    // the clock, in beats, on the music's bars
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? bpm : NO_BPM;
    const beatS = 60 / this._bpm;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > SNAP_BEATS) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else this._clock += Math.max(dt / beatS * 0.5, dt / beatS + err * Math.min(1, dt / 0.5));
    } else { this._clock += dt / beatS; this._targetOffset = null; }
    const c = this._clock, cp = this._clockPrev;
    const edge = (k) => Math.floor(cp / k) !== Math.floor(c / k) && c > cp;
    const U = this._U;

    // the rise: the 8 bars before a known drop (?morphbuild=1: always)
    const dir = profile.director;
    const nx = dir && dir.nextEvent;
    let build = 0;
    if (nx && nx.type === 'drop' && nx.score >= DROP_SCORE_MIN && nx.msUntil > 0) build = Math.max(0, 1 - nx.msUntil / 1000 / (BUILD_BEATS * beatS));
    if (this._forceBuild) build = 0.5 + 0.5 * Math.sin(this._time * 0.4);
    U.uContract.value = build;

    // the kick (every beat), the highs (the off-beat eighths); during the rise the kicks come on eighths, then sixteenths
    const kickEvery = build > 0.75 ? 0.25 : (build > 0.4 ? 0.5 : 1);
    if (edge(kickEvery)) this._lastKickS = this._time;
    if (Math.floor(cp - 0.5) !== Math.floor(c - 0.5) && c > cp) this._lastHatS = this._time;
    const bands = dir && dir.bands;
    const hasBands = !!(dir && dir.hasReliableGrid && bands && (bands.low + bands.high) > 0);
    const lowW = hasBands ? 0.4 + 0.6 * bands.low : 1;
    const highW = hasBands ? 0.3 + 0.7 * bands.high : 0.6;
    U.uSpike.value.set(spikeEnvelope(this._time - this._lastKickS) * lowW, spikeEnvelope(this._time - this._lastHatS) * highW, 0.28 * this._spikeMul, 0.40 + 0.25 * build);
    // the beat's surface wave and RGB split
    const bph = c - Math.floor(c);
    U.uWave.value.set(bph, 0.05 * Math.pow(1 - bph, 1.5), 0.06);
    this._post.update(vdjBeatChroma(bph, beatS));

    // the bar: camera kick, the shape +15 %
    if (edge(4)) this._lastBarS = this._time;
    const sinceBar = this._time - this._lastBarS;
    const barFlash = 1 + 0.15 * Math.exp(-sinceBar / 0.12);

    // the phrase: morph to the next shape, a new axis
    let phraseEdge = false;
    if (hasGrid && ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phraseEdge = true; this._phasePhrase = ph; }
    else if (edge(16)) phraseEdge = true;
    if (!phraseEdge && edge(4) && c - this._lastPhraseAt >= 32) phraseEdge = true;    // a phrase grid that never wraps
    if (phraseEdge && !this._boom && this._morphStart == null) {
      this._lastPhraseAt = c;
      this._next = this._nextShape();
      this._morphStart = c;
      this._rotAxisB = (this._axisIdx + 1) % AXES.length;
      this._note({ what: 'phrase', beat: +c.toFixed(2), to: SHAPES[this._next] });
    }

    // the drop: burst, flash, gathering into the next shape over a bar
    let drop = hasGrid && this._tickDrops(profile);
    if (this._forceDrop && edge(4) && Math.floor(c / 4) % 4 === 2) drop = true;
    if (drop && !this._boom) {
      this._boom = { start: c };
      this._next = this._nextShape(); this._morphStart = null;
      this._rotAxisB = (this._axisIdx + 1) % AXES.length;
      if (this._comfort.requestFlash(this._time)) this._flashFrames = 1;
      this._note({ what: 'drop', beat: +c.toFixed(2), to: SHAPES[this._next] });
    }

    // the rotation: a turn in 16 beats, integrated (faster during the rise)
    this._rotAngle += dt / beatS * TAU / ROT_BEATS * (1 + 2.5 * build);
    let morph = 0, boomV = [0, 0, 0];
    if (this._boom) {
      const t = c - this._boom.start;
      const burst = Math.min(1, t / BOOM_BURST_BEATS);
      const gather = Math.max(0, Math.min(1, (t - BOOM_BURST_BEATS) / BOOM_GATHER_BEATS));
      const gs = gather * gather * (3 - 2 * gather);
      boomV = [1, burst, gs];
      morph = gs;
      if (t >= BOOM_BURST_BEATS + BOOM_GATHER_BEATS) this._finishMorph();
    } else if (this._morphStart != null) {
      morph = Math.min(1, (c - this._morphStart) / MORPH_BEATS);
      if (morph >= 1) { this._finishMorph(); morph = 0; }
    }
    if (this._forceMorph != null) { morph = this._forceMorph; if (this._next == null) this._next = (this._shape + 1) % SHAPES.length; }
    U.uShapeA.value = this._shape;
    U.uShapeB.value = this._next != null ? this._next : this._shape;
    U.uMorph.value = this._next != null ? morph : 0;
    shapeRotation(this._shape, this._rotAngle, AXES[this._axisIdx], U.uRotA.value);
    U.uSpinA.value = U.uSpinB.value = (this._rotAngle / TAU) % 1;
    shapeRotation(U.uShapeB.value, this._rotAngle, AXES[this._rotAxisB], U.uRotB.value);
    U.uBoom.value.set(boomV[0], boomV[1], boomV[2]);
    U.uBeats.value = c;
    U.uScroll.value = (c / 16) % 1;
    U.uGain.value = barFlash;

    // the trails: half a beat of half-life (two during the burst)
    const half = (this._boom ? TRAIL_HALF_BOOM_BEATS : TRAIL_HALF_BEATS) * beatS;
    this._fbU.uDecay.value = Math.pow(0.5, dt / half);
    this._dispU.uFlash.value = this._flashFrames > 0 ? 1 : 0;
    if (this._flashFrames > 0) this._flashFrames--;

    // the camera: the bar's kick (zoom or roll)
    const k = vdjCameraKick(barIn != null ? barIn : (c / 4) % 1, Math.floor(c / 4), 4);
    this._cam.fov = FOV_DEG / k.zoom;
    this._cam.up.set(Math.sin(k.roll), Math.cos(k.roll), 0);
    this._cam.position.set(0, 0, CAM_D);
    this._cam.lookAt(0, 0, 0);
    this._cam.updateProjectionMatrix();
  }

  _finishMorph() {
    if (this._next != null) this._shape = this._next;
    this._axisIdx = this._rotAxisB;
    this._next = null; this._morphStart = null; this._boom = null;
  }

  // the cover and the frame, in screen units (height units, y up)
  _layout() {
    const U = this._U;
    const a = safeZoneUniforms.uAspect.value || 1.78;
    // R19 : centre, gabarit et trou des formes = le bloc de scène au centre, pas la pochette
    const c = safeZoneUniforms.uStageCenter.value, h = safeZoneUniforms.uStageHalfSize.value;
    U.uAspect.value = a;
    U.uCover.value.set(c.x, c.y, Math.max(h.x, 0.05), Math.max(h.y, 0.05));
    // le trou de garde, lui, suit la pochette réelle (coin ou centre)
    const sc = safeZoneUniforms.uSafeCenter.value, sh = safeZoneUniforms.uSafeHalfSize.value;
    U.uGuard.value.set(sc.x, sc.y, Math.max(sh.x, 0.05), Math.max(sh.y, 0.05));
    // le dégagement central : gardé quand la pochette est au centre, refermé
    // (en une demi-seconde) quand elle part dans un coin
    const away = Math.hypot(sc.x - c.x, sc.y - c.y);
    const want = 1 - Math.min(1, Math.max(0, (away - 0.1) / 0.3));
    const k = this._dt != null ? 1 - Math.exp(-this._dt / 0.5) : 1;
    U.uClear.value += (want - U.uClear.value) * k;
    U.uFrame.value.set(a - 0.1, 0.9);
  }

  _render(renderer) {
    this._renderer = renderer;      // the probe reads the trails back
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    const cam = this._cam;
    if (Math.abs(cam.aspect - w / h) > 1e-4) { cam.aspect = w / h; cam.updateProjectionMatrix(); }
    this._U.uPx.value = Math.max(0.6, h / 1080);
    if (!this._ptsTarget || this._ptsTarget.width !== w || this._ptsTarget.height !== h) {
      if (this._ptsTarget) this._ptsTarget.dispose();
      this._ptsTarget = new THREE.WebGLRenderTarget(w, h, { depthBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setClearColor(0x000000, 1);
    // 1. the points (the one draw call of points)
    renderer.setRenderTarget(this._ptsTarget);
    renderer.clear(true, false, false);
    renderer.render(this._private, cam);
    // 2. the trails: previous * decay - 1/255 + the points, at half resolution
    if (this._fb) {
      this._fb.ensureSize(renderer);
      this._fbU.uPrev.value = this._fb.read.texture;
      this._fbU.uPts.value = this._ptsTarget.texture;
      renderer.setRenderTarget(this._fb.write);
      renderer.render(this._fbScene, this._fbCam);
      this._dispU.uTrail.value = this._fb.write.texture;
      this._fb.swap();
    }
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uPts.value = this._ptsTarget.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    for (const m of [this._display, this._fbQuad, this._points]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._ptsTarget) this._ptsTarget.dispose();
    if (this._fb) this._fb.dispose();
    if (this._black) this._black.dispose();
    if (typeof window !== 'undefined' && window.__morph === this) window.__morph = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
